// A car's settings, as its TUNE MFD's menu has them: Units, Vehicle, Service (on its Trips and service page) and
// Datalogs, in its words and the car's units. Changes wait in the save bar, and go from the revision read: when the MFD
// changed the settings meanwhile, the fields it touched say what it set, and saving again keeps the owner's.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unitsOf, type components, type Units } from '@tune/shared';
import { Fragment, Suspense, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, data, problem } from '../../api';
import { useAuth } from '../../auth';
import { lazyPage } from '../../lazy';
import { agoText } from '../../lib/words';
import { keys, useSettings } from '../../queries';
import {
  Banner,
  Button,
  ErrorBanner,
  ListButton,
  ListControl,
  ListField,
  ListLink,
  ListSection,
  ListSelect,
  SaveBar,
  SegmentedControl,
  Sheet,
  Skeleton,
  Switch,
  useConfirm,
  useNow,
  useRouteSheet,
  useToast,
  useUnsavedChanges,
} from '../../ui';
import { CaretRightIcon, ClockCounterClockwiseIcon, MinusIcon, PlusIcon } from '../../ui/icons';
import { CarPage } from './CarPage';
import {
  describe,
  EVERY_HOURS,
  EVERY_KM,
  LOG_VALUES,
  MOST_GEARS,
  ordinal,
  problemWith,
  RATE,
  REMIND_BY,
  shown,
  stored,
  typedNumber,
  unitOf,
  UNITS,
  VEHICLE,
  type Field,
  type NumberField,
} from './settings-fields';
import s from './Settings.module.css';

type Values = Record<string, unknown>;
type Vehicle = components['schemas']['Vehicle'];
type VehicleSettings = components['schemas']['VehicleSettings'];

const chevron = <CaretRightIcon size={16} weight="bold" aria-hidden="true" />;

// Choosing datalog values brings TUNE Live's value catalogue and config reader, in a chunk of their own: it starts
// loading as the page opens, so it's there when it's asked for, and nothing else waits for it.
const ValuesSheet = lazyPage(
  () => import('../values/ValuesSheet'),
  (m) => m.ValuesSheet,
);

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function Settings() {
  return <CarPage section="Settings">{(car) => <SettingsOf key={car.id} car={car} />}</CarPage>;
}

function SettingsOf({ car }: { car: Vehicle }) {
  const settings = useSettings(car.id);
  if (settings.data) {
    return <Editor car={car} settings={settings.data} />;
  }
  return settings.isError ? (
    <ErrorBanner retry={() => void settings.refetch()}>The car's settings couldn't load.</ErrorBanner>
  ) : (
    <Skeleton blocks={4} lines={0} label="Loading the car's settings" />
  );
}

function Editor({ car, settings }: { car: Vehicle; settings: VehicleSettings }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const say = useToast();
  const confirm = useConfirm();
  const now = useNow(60_000);
  const [draft, setDraft] = useState<Values>({});
  /** Numbers as typed, so a half-typed one stays as it is. */
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Fields the MFD changed since this page read them, which the owner changed too. */
  const [clashes, setClashes] = useState<string[]>([]);
  const [gearsOpen, setGearsOpen] = useRouteSheet('gears');
  const [valuesOpen, setValuesOpen] = useRouteSheet('values');
  const base = settings.values;
  const value = (key: string) => (key in draft ? draft[key] : base[key]);
  const units = useMemo(() => unitsOf({ ...base, ...draft }), [base, draft]);
  const changes = Object.keys(draft).length;
  useUnsavedChanges(changes > 0, 'your changes to the settings');

  const set = (key: string, next: unknown) => {
    setErrors(({ [key]: _gone, ...rest }) => rest);
    setDraft((d) => {
      const { [key]: _old, ...rest } = d;
      return same(next, base[key]) ? rest : { ...rest, [key]: next };
    });
    // A new unit shows its numbers again, in that unit.
    if (key === 'speedUnit' || key === 'distanceUnit') {
      setTexts(({ topSpeed: _t, serviceIntervalKm: _s, ...rest }) => rest);
    }
  };
  const discard = () => {
    setDraft({});
    setTexts({});
    setErrors({});
    setClashes([]);
    save.reset();
  };

  const save = useMutation({
    mutationFn: (values: Values) =>
      data(
        api.PATCH('/vehicles/{vehicleId}/settings', {
          params: { path: { vehicleId: car.id }, header: { 'If-Match': `"${settings.rev}"` } },
          body: { values },
        }),
      ),
    onSuccess: (answer) => {
      queryClient.setQueryData(keys.settings(car.id), answer);
      void queryClient.invalidateQueries({ queryKey: keys.history(car.id) });
      setDraft({});
      setTexts({});
      setClashes([]);
      say('Saved. The MFD takes the changes once the car is stopped.');
    },
    onError: (error, sent) => {
      if (error instanceof ApiError && error.code === 'changed') {
        const changed = (error.body.changed as string[] | undefined) ?? [];
        queryClient.setQueryData<VehicleSettings>(keys.settings(car.id), (old) =>
          old
            ? { ...old, rev: Number(error.body.rev), values: (error.body.values as Values | undefined) ?? old.values }
            : old,
        );
        setClashes(changed.filter((k) => k in sent));
      }
    },
  });

  // A clash: the first field it touched comes into view.
  const firstClash = useRef<string | null>(null);
  useEffect(() => {
    const key = clashes[0];
    if (key && key !== firstClash.current) {
      document.querySelector(`[data-setting="${key}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    firstClash.current = key ?? null;
  }, [clashes]);

  const submit = () => {
    const found: Record<string, string> = {};
    for (const [key, v] of Object.entries(draft)) {
      const wrong = problemWith(key, v);
      if (wrong) {
        found[key] = wrong;
      }
    }
    setErrors(found);
    if (Object.keys(found).length === 0 && changes > 0) {
      save.mutate(draft);
    } else {
      document.querySelector(`[data-setting="${Object.keys(found)[0]}"]`)?.scrollIntoView({ block: 'center' });
    }
  };

  const conflict = save.error instanceof ApiError && save.error.code === 'changed';
  const row = (f: Field) => (
    <SettingRow
      key={f.key}
      field={f}
      value={value(f.key)}
      units={units}
      text={texts[f.key]}
      error={errors[f.key]}
      clash={clashes.includes(f.key) ? describe(f, base[f.key], units) : null}
      onText={(t) => setTexts((x) => ({ ...x, [f.key]: t }))}
      onChange={(v) => set(f.key, v)}
      onLock={async (on) => {
        if (on) {
          set(f.key, true);
          return;
        }
        const yes = await confirm({
          title: 'Unlock the menu while driving?',
          message: 'Every screen, and typing, will work while the car moves. Leave them to a passenger.',
          confirm: 'Unlock',
        });
        if (yes) {
          set(f.key, false);
        }
      }}
      onOpen={() => (f.kind === 'gears' ? setGearsOpen(true) : setValuesOpen(true))}
    />
  );
  const byHours = value('serviceByHours') === true;
  const empty = Object.keys(base).length === 0;

  return (
    <>
      <p className={s.lead}>
        Changes here reach the car's TUNE MFD at its next sync, and it takes them once the car is stopped.
        {settings.updatedAt ? ` Last changed ${agoText(settings.updatedAt, now)}.` : ''}
      </p>
      {empty && (
        <Banner tone="info">
          No settings yet. They come from the car's MFD once it signs in with its own. Whatever you set here, the MFD
          takes.
        </Banner>
      )}
      {save.isError && (
        <Banner tone="error">
          {conflict
            ? clashes.length > 0
              ? 'The settings changed on the MFD meanwhile. Check the fields marked, then save again: saving keeps yours.'
              : 'The settings changed on the MFD meanwhile, but not yours. Save again to keep yours.'
            : problem(save.error)}
        </Banner>
      )}
      <div className={s.columns}>
        <div className={s.column}>
          <ListSection header="Units">{UNITS.map(row)}</ListSection>
          <ListSection header="Vehicle">{VEHICLE.map(row)}</ListSection>
        </div>
        <div className={s.column}>
          <ListSection header="Service" footer="On the MFD, these are on Trips and service.">
            {row(REMIND_BY)}
            {row(byHours ? EVERY_HOURS : EVERY_KM)}
          </ListSection>
          <ListSection header="Datalogs" footer="Where each MFD saves its datalogs is set on that MFD.">
            {row(RATE)}
            {row(LOG_VALUES)}
          </ListSection>
          <ListSection>
            <ListLink
              to={`/garage/${car.id}/settings/history`}
              icon={<ClockCounterClockwiseIcon size={20} weight="bold" />}
              label="History"
              subtitle="Every change, newest first, and any restored"
            />
          </ListSection>
        </div>
      </div>
      <GearsSheet
        open={gearsOpen}
        ratios={Array.isArray(value('gearRatios')) ? (value('gearRatios') as number[]) : []}
        onClose={() => setGearsOpen(false)}
        onDone={(ratios) => {
          set('gearRatios', ratios);
          setGearsOpen(false);
        }}
      />
      <Suspense fallback={null}>
        <ValuesSheet
          vehicleId={car.id}
          open={valuesOpen}
          value={Array.isArray(value('logValues')) ? (value('logValues') as string[]) : null}
          onClose={() => setValuesOpen(false)}
          onDone={(chosen) => {
            set('logValues', chosen);
            setValuesOpen(false);
          }}
        />
      </Suspense>
      <SaveBar
        dirty={changes > 0}
        busy={save.isPending}
        message={
          conflict && clashes.length > 0 ? 'Check the fields marked' : changes === 1 ? '1 change' : `${changes} changes`
        }
        saveLabel="Save changes"
        onSave={submit}
        onDiscard={discard}
      />
    </>
  );
}

/** One setting as a row: a choice, a few in a segmented control, a number, a switch, or a row that opens a sheet. */
function SettingRow({
  field,
  value,
  units,
  text,
  error,
  clash,
  onText,
  onChange,
  onLock,
  onOpen,
}: {
  field: Field;
  value: unknown;
  units: Units;
  text: string | undefined;
  error: string | undefined;
  clash: string | null;
  onText: (text: string) => void;
  onChange: (value: unknown) => void;
  onLock: (on: boolean) => Promise<void>;
  onOpen: () => void;
}) {
  const id = useId();
  const notes = [error ? `${id}-error` : '', clash ? `${id}-clash` : ''].filter(Boolean).join(' ') || undefined;
  const note = (error || clash) && (
    <div className={s.notes}>
      {clash && (
        <p id={`${id}-clash`} className={s.clash}>
          Changed on the MFD to {clash} meanwhile. Saving keeps yours.
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className={s.error}>
          {error}
        </p>
      )}
    </div>
  );
  let control: ReactNode;
  switch (field.kind) {
    case 'choice':
      control = (
        <ListSelect
          label={field.label}
          value={value === undefined ? '' : String(value)}
          aria-describedby={notes}
          onChange={(e) => onChange(field.choices.find((c) => String(c.value) === e.target.value)?.value)}
        >
          {value === undefined && <option value="">Not set</option>}
          {field.choices.map((c) => (
            <option key={String(c.value)} value={String(c.value)}>
              {c.label}
            </option>
          ))}
        </ListSelect>
      );
      break;
    case 'segments':
      // Its own row: a segmented control takes the room it needs, and goes under its label on a narrow phone.
      control = (
        <div className={s.segmentRow}>
          <span aria-hidden="true">{field.label}</span>
          <SegmentedControl
            label={field.label}
            segments={field.choices.map((c) => ({ value: String(c.value), label: c.label }))}
            value={value === undefined ? '' : String(value)}
            onChange={(v) => onChange(field.choices.find((c) => String(c.value) === v)?.value)}
          />
        </div>
      );
      break;
    case 'number': {
      const unit = unitOf(field, units);
      control = (
        <ListField
          label={
            <>
              {field.label}
              {unit && <span className="visually-hidden"> ({unit})</span>}
            </>
          }
          suffix={unit ?? undefined}
          inputMode="decimal"
          autoComplete="off"
          value={text ?? (typeof value === 'number' ? shown(field, units, value) : '')}
          placeholder={value === undefined ? 'Not set' : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={notes}
          onChange={(e) => {
            onText(e.target.value);
            onChange(stored(field as NumberField, units, e.target.value));
          }}
        />
      );
      break;
    }
    case 'switch':
      control = (
        <ListControl
          label={field.label}
          subtitle={value === false ? 'Every screen, and typing, works while the car moves.' : undefined}
          control={
            <Switch
              checked={value !== false}
              aria-describedby={notes}
              onChange={(e) => void onLock(e.target.checked)}
            />
          }
        />
      );
      break;
    case 'gears': {
      const ratios = Array.isArray(value) ? (value as number[]) : [];
      control = (
        <ListButton
          label="Gear ratios"
          subtitle={ratios.length > 0 ? ratios.join(' · ') : 'Not set'}
          detail={ratios.length === 1 ? '1 gear' : `${ratios.length} gears`}
          accessory={chevron}
          onClick={onOpen}
        />
      );
      break;
    }
    case 'logValues':
      control = (
        <ListButton label="Values" subtitle={describe(field, value, units)} accessory={chevron} onClick={onOpen} />
      );
      break;
  }
  return (
    <Fragment key={field.key}>
      <div data-setting={field.key}>{control}</div>
      {note}
    </Fragment>
  );
}

/** The gear ratios, a gear at a time, with a gear added or the top one taken off. Nothing changes until Done. */
function GearsSheet({
  open,
  ratios,
  onClose,
  onDone,
}: {
  open: boolean;
  ratios: readonly number[];
  onClose: () => void;
  onDone: (ratios: number[]) => void;
}) {
  const [texts, setTexts] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Each time it opens, it starts from the ratios now.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTexts(ratios.map(String));
      setError(null);
    }
    wasOpen.current = open;
  }, [open, ratios]);
  const done = () => {
    const numbers = texts.map(typedNumber);
    const wrong = problemWith('gearRatios', numbers);
    if (wrong) {
      setError(wrong);
      return;
    }
    onDone(numbers);
  };
  const last = typedNumber(texts.at(-1) ?? '');
  return (
    <Sheet open={open} onClose={onClose} title="Gear ratios" action={{ label: 'Done', onClick: done }}>
      <p className={s.about}>
        The MFD works out the gear from these, the final drive, the tyres, the speed and the RPM.
      </p>
      {error && <Banner tone="error">{error}</Banner>}
      {texts.length > 0 ? (
        <ListSection level={3}>
          {texts.map((t, i) => (
            <ListField
              key={i}
              label={`${ordinal(i + 1)} gear`}
              inputMode="decimal"
              autoComplete="off"
              value={t}
              onChange={(e) => setTexts((all) => all.map((x, j) => (j === i ? e.target.value : x)))}
            />
          ))}
        </ListSection>
      ) : (
        <p className={s.about}>No gears yet.</p>
      )}
      <div className={s.gearActions}>
        {texts.length < MOST_GEARS && (
          <Button
            variant="gray"
            size="small"
            icon={<PlusIcon size={16} weight="bold" />}
            onClick={() => setTexts((all) => [...all, String(last > 0 ? Math.round(last * 0.8 * 100) / 100 : 1)])}
          >
            Add a gear
          </Button>
        )}
        {texts.length > 1 && (
          <Button
            variant="gray"
            size="small"
            icon={<MinusIcon size={16} weight="bold" />}
            onClick={() => setTexts((all) => all.slice(0, -1))}
          >
            Remove the top gear
          </Button>
        )}
      </div>
    </Sheet>
  );
}
